'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { loadCanvasLibrary } from '@/lib/canvas/load-library';
import { useAuth } from '@/app/auth-provider';
import { CANVAS_AVAILABILITY_CHANGED_EVENT, type CanvasArtifactSummary } from '@/lib/canvas/shared';

export function useProjectCanvasAssets(projectId: string | null, enabled: boolean) {
  const { user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const userId = user?.uid;
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState<{
    projectId: string;
    userId: string;
    assets: CanvasArtifactSummary[];
    error: string | null;
    loading: boolean;
    available: boolean | null;
  } | null>(null);
  const refresh = useCallback(() => setRevision(current => current + 1), []);

  useEffect(() => {
    const currentUser = userRef.current;
    if (!enabled || !projectId || !userId || !currentUser) {
      setResult(null);
      return;
    }
    const controller = new AbortController();
    const scope = { projectId, userId };
    setResult(current => ({
      ...scope,
      assets: current?.projectId === projectId && current.userId === userId ? current.assets : [],
      error: null,
      loading: true,
      available: null,
    }));
    const shareAvailability = (enabled: boolean) => {
      window.dispatchEvent(
        new CustomEvent(CANVAS_AVAILABILITY_CHANGED_EVENT, {
          detail: { ...scope, enabled },
        })
      );
    };
    void (async () => {
      try {
        const token = await currentUser.getIdToken();
        if (controller.signal.aborted) return;
        const library = await loadCanvasLibrary(projectId, token, controller.signal);
        if (controller.signal.aborted) return;
        shareAvailability(library.available);
        setResult({ ...scope, ...library, error: null, loading: false });
      } catch (error) {
        if (controller.signal.aborted) return;
        setResult({
          ...scope,
          assets: [],
          available: null,
          error: error instanceof Error ? error.message : 'Couldn’t load assets.',
          loading: false,
        });
      }
    })();
    window.addEventListener('focus', refresh);
    return () => {
      controller.abort();
      window.removeEventListener('focus', refresh);
    };
  }, [enabled, projectId, userId, revision, refresh]);

  const current =
    enabled && result?.projectId === projectId && result?.userId === userId ? result : null;
  return {
    assets: current?.assets || [],
    available: enabled ? (current?.available ?? null) : false,
    loading: Boolean(enabled && projectId && userId && (!current || current.loading)),
    error: current?.error || null,
    refresh,
  };
}
